/// <amd-module name="Widgets/Configs/PullrequestWidgetConfig/PullRequestWidgetConfig" />
import * as React from "react";
import { isNameValid, NameField } from "Dashboards/CommonConfig/NameField";
import { SizePicker } from "Dashboards/CommonConfig/SizePicker";
import { ICacheableQueryService, IDashboardsCacheableQueryServiceName } from "Dashboards/WidgetCommon/DashboardsCacheableQueryService";
import { ConfigProps, LoadingStatus } from "Dashboards/WidgetContracts/Internal/ConfigProps";
import { DashboardWidgetProps, WidgetGridSize } from "Dashboards/WidgetContracts/Internal/DashboardWidgetProps";
import { WebApiTeam } from "Tfs/Platform/Contracts/Core";
import { IProjectData, ITfsPageService } from "Tfs/Platform/Page";
import { IVssPageContext } from "VSS/Platform/Context";
import { VssComponent } from "VSS/Platform/Layout";
import { IListSelection } from "VSSUI/List";
import { FormItem } from "VSSUI/FormItem";
import { IListBoxItem } from "VSSUI/ListBox";
import { Dropdown } from "VSSUI/Dropdown";
import { DropdownMultiSelection, DropdownSelection } from "VSSUI/Utilities/DropdownSelection";
import { GenericSelectionDropdown } from "Widgets/Configs/Common/GenericSelectionDropdown";
import { convertToListBoxItems, OpStoreTeamsQuery } from "Widgets/Configs/Common/OpStoreTeamsQuery";
import * as Resources from "Widgets/Configs/PullrequestWidgetConfig/Resources";
import { PullRequestWidgetSettings } from "WidgetCommon/ConfigContracts/PullRequestsWidgetSettings";
import { PullRequestManager, QueryType, PullRequestWidgetConstants } from "./PullRequestManager";
import { WidgetSettingsManagerProps } from "WidgetCommon/ModernWidgetTypes/SettingsManagerBase";
import { IRepositoryDropdownItem } from "Repos/RepoDropdown/RepositoryDropdownProvider";
import { RepositoryDropdown } from "Repos/RepoDropdown/Components/RepositoryDropdown";
import { GitRepository } from "Repos/Common/Contracts/Git";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import * as Utils_String from "VSS/Core/Util/String";

interface PullRequestWidgetConfigState {
    name: string;
    size: WidgetGridSize;
    widgetSettings: PullRequestWidgetSettings;
    teamOptions: IListBoxItem<WebApiTeam>[];
    queryTypeValue: string;
    repos?: IListBoxItem<IRepositoryDropdownItem>[];
    selectedRepos: GitRepository[] | undefined;
    initialLoadComplete: boolean;
    showRepositorySelectionError: boolean
}

export class PullRequestWidgetConfig extends VssComponent<ConfigProps, PullRequestWidgetConfigState> {

    public static readonly componentType: string = "PullRequestWidgetConfig";
    private repositorySelectionLimit : number | undefined = PullRequestWidgetConstants.DEFAULT_MAX_REPOSITORY_SELECTION;
    private selection: DropdownMultiSelection;
    private projectId: string;
    private projectName: string;
    private parsedWidgetSettings: PullRequestWidgetSettings | null;
    private defaultViewSelection: IListSelection = new DropdownSelection();
    private performanceService : IVssPerformanceService;
    private resolvedRepositoryDropdownItems: IListBoxItem<IRepositoryDropdownItem>[] = [];

    private ViewPickerModel: Array<IListBoxItem<{}>> = [
        { text : Resources.Filter_AssignedToTeam, id : QueryType.AssignedToTeam.toString() },
        { text : Resources.Filter_AssignedToMe, id : QueryType.AssignedToMe.toString() },
        { text : Resources.Filter_CreatedByMe, id : QueryType.CreatedByMe.toString() }
    ];

    private static isConfigValid(config: DashboardWidgetProps, teamOptions: IListBoxItem<WebApiTeam>[], widgetSettings: PullRequestWidgetSettings): boolean {
        return (
            isNameValid(config.name) &&
            (widgetSettings === null || teamOptions.some(teamOption => teamOption.id === widgetSettings.teamId)) &&
            (widgetSettings === null || widgetSettings.queryType !== undefined || widgetSettings.queryType > 0) &&
            (widgetSettings === null || widgetSettings.selectedRepos !== undefined && widgetSettings.selectedRepos.length > 0)
        );
    }

    public constructor(props: ConfigProps) {
        super(props);
        this.state = { 
            name: this.props.config.name,
            size: this.props.config.size,
            widgetSettings: {} as PullRequestWidgetSettings,
            teamOptions: [],
            queryTypeValue: '',
            repos: [] as GitRepository[],
            selectedRepos: [] as GitRepository[],
            initialLoadComplete: false,
            showRepositorySelectionError: false
        };

        this.selection = new DropdownMultiSelection();
    }

    public async componentDidMount(): Promise<void> {
        try{
            this.performanceService = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
            const widgetProps = this.props.config as WidgetSettingsManagerProps;
            const returnIncompleteSettings = true;
            this.projectId = this.getProject()?.id;
            this.projectName = this.getProject()?.name;
            const teamOptions = await this.trackPromise(this.getTeamListItems(this.context.pageContext, this.projectId)).promise;
            const settingsManager = new PullRequestManager(teamOptions, this.props.teamContext.id);
            const widgetSettings = await settingsManager.ensureInitialSettings(widgetProps, returnIncompleteSettings);
            this.repositorySelectionLimit = PullRequestWidgetConstants.DEFAULT_MAX_REPOSITORY_SELECTION;
            this.setState(
                {
                    name: widgetProps.name,
                    widgetSettings: widgetSettings,
                    size: this.props.config.size,
                    teamOptions: teamOptions,
                    initialLoadComplete: true,
                    selectedRepos: widgetSettings.selectedRepos
                },
                () => {
                    this.props.parentProps.onConfigLoaded({ outcome: LoadingStatus.success }); // The configuration experience becomes visible after this point.
                }
            );
        } catch (e) {
            if (e.isCanceled) {
                return; // Do nothing if the promises were canceled
            }

            this.props.parentProps.onConfigLoaded({
                outcome: LoadingStatus.failure,
                outcomeDescription: e
            });
        }
    }

    public componentDidUpdate(prevProps: ConfigProps, prevState: PullRequestWidgetConfigState): void {
        if (prevState.teamOptions == null && this.state.teamOptions != null) {
            this.props.parentProps.onConfigLoaded();
        }

        if (prevProps.saveValidationTimeStamp != this.props.saveValidationTimeStamp) {
            const currentConfig = this.props.config;
            this.props.parentProps.onValidationResponse(
                currentConfig,
                PullRequestWidgetConfig.isConfigValid(currentConfig, this.state.teamOptions!, this.state.widgetSettings)
            );
        }

        if(this.resolvedRepositoryDropdownItems !== this.state.repos){
            this.setState({repos: this.resolvedRepositoryDropdownItems});
        }
    }

    public render(): JSX.Element | null {

        if (!this.state.initialLoadComplete) {
            return null;
        }

        if (this.state.teamOptions != null) {
            this.parsedWidgetSettings = this.state.widgetSettings;
            this.applyDefaultViewSelection();
            
            return (
                <div className="pull-request-widget-config">
                    <NameField value={this.state.name} onChange={this.onNameChanged} />

                    {this.renderSizeControl()}

                    {this.renderTeamsControl()}

                    {this.renderRepositoryControl()}

                    {this.renderDefaultViewControl()} 
                </div>
            );
        }
        return null;
    }

    /** EVENT HANDLERS */
    /* Pass Name changes up to parent. */
    private onNameChanged = (newName: string): void => {
        this.setState({ name: newName }, this.notifyParentOfUpdate);
    };

    /* Pass Size changes up to parent. */
    private onSizeChanged = (newSize: WidgetGridSize): void => {
        this.setState({ size: newSize }, this.notifyParentOfUpdate);
    };

    /* Pass Team Id up to the parent. */
    private onTeamSelect = (teamItem: IListBoxItem<WebApiTeam>): void => {
        const newSettings = { ...this.state.widgetSettings };
        newSettings.teamId = teamItem.id;

        this.props.parentProps.onUpdate({
            ...this.props.config,
            settings: JSON.stringify(newSettings)
        });

        this.setState({ widgetSettings: newSettings, initialLoadComplete: true }, this.notifyParentOfUpdate);
    };

    /* Pass Default View data up to the parent. */
    private onViewSelect = (event: React.SyntheticEvent<HTMLElement>, item: IListBoxItem<{}>): void => {
        const newSettings = { ...this.state.widgetSettings };
        newSettings.queryType = Number(item.id);

        this.props.parentProps.onUpdate({
            ...this.props.config,
            settings: JSON.stringify(newSettings)
        });

        this.setState({ widgetSettings: newSettings }, this.notifyParentOfUpdate);
    };

    // update this.selectedRepos on change in RepositoryDropdown item selection
    private onRepositoryChange = (event: React.SyntheticEvent<HTMLElement>, item: IListBoxItem<IRepositoryDropdownItem>): void => {
        if (!item.data) {
            return;
        }

        // return error if it is a selection event and selectedRepos.length equals max repo selection limit
        if(this.state.selectedRepos && this.repositorySelectionLimit &&
            this.state.selectedRepos.length >= this.repositorySelectionLimit &&
            !this.state.selectedRepos.some(repo => repo.id === item.id)){
            if(this.state.repos){
                const repoIndex = this.state.repos.findIndex(repo => repo.id === item.id);
                this.selection.toggle(repoIndex);
                this.setState({showRepositorySelectionError: true});
            }
            return;
        }
        const repository = item.data.repository ?? this.state.repos?.find(r => r.id === item.data!.id)?.data?.repository;
        
        if(repository){
            const existingRepos = this.state.selectedRepos ?? [];
            const selectedRepoIndex = existingRepos.findIndex(repo => repo.id === repository.id);
            if(selectedRepoIndex > -1){
                existingRepos.splice(selectedRepoIndex,1)
            }
            else{
                existingRepos.push(repository);
            }
            this.setState({selectedRepos:  existingRepos.length > 0 ? existingRepos : undefined})
        }
        this.setState({showRepositorySelectionError: false});
    };

    // returns resolved dropdown items 
    private getItemProvider = (values: IListBoxItem<IRepositoryDropdownItem>[]) => {
        this.resolvedRepositoryDropdownItems = values;
    }

    /* save selected repos in widgetSettings on dropdown collapse */
    private onDropdownCollapse = () => {
        this.performanceService.startScenario("pull-request-widget-selected-repo-count", false);
        const newSettings = { ...this.state.widgetSettings };
        newSettings.selectedRepos = this.state.selectedRepos;
        this.props.parentProps.onUpdate({
            ...this.props.config,
            settings: JSON.stringify(newSettings)
        });
        this.setState({ widgetSettings: newSettings }, this.notifyParentOfUpdate);
        this.performanceService.endScenario("Dashboards","pull-request-widget-new-selected-repo-count", false, undefined, 
        { 
            "widgetId": this.getWidgetId,
            "widgetNme": "PullRequestWidgetNew",
            "selectedRepoCount":this.state.selectedRepos ? this.state.selectedRepos.length:0
        });
    }

    /** Render Functions */
    private renderSizeControl(): string | number | boolean | {} | React.ReactElement<any, string | React.JSXElementConstructor<any>> | React.ReactNodeArray | React.ReactPortal | null | undefined {
        return this.props.contributionConstraints.supportedSizes.length > 1 && (
            <SizePicker
                key="size-picker"
                onChange={this.onSizeChanged}
                sizes={this.props.contributionConstraints.supportedSizes}
                value={this.props.config.size} />
        );
    }

    private renderDefaultViewControl() {
        return <FormItem label={Resources.ViewLabel} className="widget-config-form-item">
            <Dropdown
                className="aggregation-picker"
                items={this.ViewPickerModel}
                selection={this.defaultViewSelection}
                onSelect={this.onViewSelect} />
        </FormItem>;
    }

    private renderRepositoryControl() {
        return (
            <FormItem label={Resources.RepoSelectorLabel} className="widget-config-form-item">
                { this.renderRepoSelectionLimitMessage() }

                <RepositoryDropdown
                    className="repository-dropdown"
                    useDefaultCalloutWidth={false}
                    dropdownSelection={this.selection}
                    providerOptions={{
                        projectId: this.projectId,
                        projectName: this.projectName
                    }}
                    onSelect={this.onRepositoryChange}
                    onCollapse={this.onDropdownCollapse}
                    getItemProvider={this.getItemProvider}
                    selectedRepositoryItems={this.state.selectedRepos} 
                />
            </FormItem>
        );  
    }

    private renderRepoSelectionLimitMessage = (): JSX.Element => {
        var role = "message";
        var className = "inline-configuration-message";

        if(this.state.showRepositorySelectionError){
            role ="alert"
            className = "inline-configuration-error";
        }

        return (
            <div className={className}>
                <span role={role}>
                    {Utils_String.format(Resources.Max_Repository_Error, this.repositorySelectionLimit)}
                </span>
            </div>
        );
    }

    private renderTeamsControl() {
        return <FormItem label={Resources.TeamLabel} className="widget-config-form-item">
            <GenericSelectionDropdown
                className="flex-grow"
                items={this.state.teamOptions}
                selectedId={this.parsedWidgetSettings ? this.parsedWidgetSettings.teamId : undefined}
                onSelect={this.onTeamSelect} />
        </FormItem>;
    }

    /** Private Functions */
    /* Get teams data. */
    private async getTeamListItems(pageContext: IVssPageContext, projectId: string): Promise<IListBoxItem<WebApiTeam>[]> {
        this.performanceService.startScenario("pull-request-widget-get-teams-list", false);
        const queryCache = pageContext.getService<ICacheableQueryService>(IDashboardsCacheableQueryServiceName);
        const teams = await queryCache.getCacheableQueryResult(OpStoreTeamsQuery.onProject(projectId));
        this.performanceService.endScenario("Dashboards","pull-request-widget-get-teams-list", false, undefined, 
        { 
            "widgetId": this.getWidgetId,
            "widgetNme": "PullRequestWidgetNew",
            "teamsCount": teams.length
        });
        return convertToListBoxItems(teams);
    }

    /* Get project */
    private getProject(): IProjectData {
        const tfsPageService = this.context.pageContext.getService<ITfsPageService>("ITfsPageService");
        const tfsPageData = tfsPageService.getData();
        if (!tfsPageData || !tfsPageData.project) {
            throw new Error("Couldn't fetch project information.");
        }
        return tfsPageData.project;
    }

    /* Notify parent when Widget Settings gets updated. */
    private notifyParentOfUpdate = (): void => {
        const updatedConfig = {
            ...this.props.config,
            name: this.state.name,
            size: this.state.size,
            hasError: false,
            settings: JSON.stringify(this.state.widgetSettings)
        };
        this.props.parentProps.onUpdate(updatedConfig);
    };

    /* Get default view selected value. */
    private applyDefaultViewSelection(){
        const defaultViewId: number = this.parsedWidgetSettings && this.parsedWidgetSettings.queryType? this.parsedWidgetSettings.queryType:QueryType.AssignedToTeam;
        const selectionPosition = this.ViewPickerModel.findIndex(item => {
            return item.id === defaultViewId.toString();
        });
        if (selectionPosition >= 0) {
            this.defaultViewSelection.select(selectionPosition, 1, false);
        } else {
            this.defaultViewSelection.clear();
        }
    }

    /* Get Widget Id */
    private getWidgetId = (): Promise<string> => {
        return Promise.resolve(this.props.config.id!);
    };
}

VssComponent.register(PullRequestWidgetConfig.componentType, PullRequestWidgetConfig);
